<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="w-full mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Production Orders</h4>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="w-full mx-auto">
        <div class="space-y-16">
          <VList :meta="meta">
            <template #before>
              <VListHeader />
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="productionOrders.length" #default>
              <template v-for="productionOrder in productionOrders">
                <VListLine
                  :key="`productionOrders_${productionOrder.uuid}`"
                  :production-order="productionOrder"
                  :disabled="isLoading"
                  @production-order-item:view="viewProductionOrderItem"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>

    <portal v-if="selectedProductionOrderItemId" to="app-modal">
      <ProductionOrderItemModal
        :id="selectedProductionOrderItemId"
        @production-order-item-modal:closed="selectedProductionOrderItemId = null"
      />
    </portal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import store from '@/store';
import VList from '@/components/Common/VList.vue';
import VListHeader from '@/components/ProductionOrders/VListHeader.vue';
import VListLine from '@/components/ProductionOrders/VListLine.vue';
import ProductionOrderItemModal from '@/components/ProductionOrders/ProductionOrderItemModal.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

const includes = 'order_items,provider,project';

export default {
  name: 'ProductionOrderItems',

  components: {
    VList,
    VListHeader,
    VListLine,
    ProductionOrderItemModal,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    status: STATUS_IDLE,
    selectedProductionOrderItemId: null,
    filters: {},
    filtersVisible: false,
  }),

  computed: {
    ...mapState('ui/productionOrders', ['meta']),
    ...mapGetters('ui/productionOrders', ['productionOrders']),
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  watch: {
    $route: 'fetchData',
  },

  async beforeRouteEnter(to, from, next) {
    const {
      query: { sort = '', ...restQuery },
    } = to;

    const sortParams = sort.split(',');
    sortParams.push('-created_at');

    try {
      await store.dispatch('ui/productionOrders/load', {
        params: {
          ...restQuery,
          filter: {
            account_relation_with: to.params.accountId,
          },
          include: includes,
          sort: sortParams.join(','),
        },
      });

      next();
    } catch (error) {
      next(false);
    }
  },

  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },

  methods: {
    ...mapMutations('ui/productionOrders', ['setIds', 'setMeta', 'setLinks']),

    async fetchData() {
      const {
        query: { sort = '', ...restQuery },
      } = this.$route;

      const sortParams = sort.split(',');
      sortParams.push('-created_at');

      this.status = STATUS_PENDING;

      try {
        await this.$store.dispatch({
          type: 'ui/productionOrders/load',
          params: {
            ...restQuery,
            filter: {
              related_account_uuid: this.accountId,
            },
            include: includes,
            sort: sortParams.join(','),
          },
        });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },

    viewProductionOrderItem(productionOrderItemUuid) {
      this.selectedProductionOrderItemId = productionOrderItemUuid;
    },
  },
};
</script>
